import React from 'react';
import PropTypes from 'prop-types';
import Container from 'reactstrap/lib/Container';
import classnames from 'classnames';
import { get, noop } from 'lodash-es';

import { parseContent } from 'client/data/cms/content';
import { CmsEntities } from 'client/data/models/cms';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';

import { VehicleEntities, VehicleModel } from 'client/data/models/vehicle-v2';
import { ContentFragment } from 'site-modules/shared/components/content-fragment/content-fragment';
import { ComparatorPreviewTable } from './comparator-preview-table';

import './comparator-preview-module.scss';

const MAX_COMPARE_SIZE = 4;
const VEHICLE_PATH_NAME = 'veh';
const DEVICE_ID = {
  false: 'Wired',
  true: 'Mobile',
};

export function ComparatorModuleContent({
  type,
  content,
  columns: propColumns,
  veh1 = null,
  veh2 = null,
  veh3 = null,
  veh4 = null,
  isMobile = false,
  className = null,
}) {
  const { title, summary } = content;
  const columns = propColumns.filter(({ styleId }) => styleId);
  const styles = [veh1, veh2, veh3, veh4].filter(veh => veh);

  if (!styles.length || styles.length !== columns.length) {
    return null;
  }

  return (
    <div className={classnames('comparator-preview-module py-2', className)}>
      <Container>
        <h2 className="heading-3 mb-1">{title}</h2>
        {summary && (
          <ContentFragment componentToUse="div" classes="mb-2">
            {summary}
          </ContentFragment>
        )}
        <ComparatorPreviewTable type={type} columns={columns} styles={styles} isMobile={isMobile} />
      </Container>
    </div>
  );
}

ComparatorModuleContent.propTypes = {
  type: PropTypes.string.isRequired,
  content: CmsEntities.Content.isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      styleId: PropTypes.string,
    })
  ).isRequired,
  veh1: VehicleEntities.Style,
  veh2: VehicleEntities.Style,
  veh3: VehicleEntities.Style,
  veh4: VehicleEntities.Style,
  isMobile: PropTypes.bool,
  className: PropTypes.string,
};

export const ComparatorModuleWrapper = connectToModel(ComparatorModuleContent, {
  veh1: bindToPath(({ columns }) => columns[0].styleId && `styles.${columns[0].styleId}`, VehicleModel),
  veh2: bindToPath(({ columns }) => columns[1].styleId && `styles.${columns[1].styleId}`, VehicleModel),
  veh3: bindToPath(({ columns }) => columns[2].styleId && `styles.${columns[2].styleId}`, VehicleModel),
  veh4: bindToPath(({ columns }) => columns[3].styleId && `styles.${columns[3].styleId}`, VehicleModel),
});

export function ComparatorPreviewModule({ content, isMobile = false, className = null, ...restProps }) {
  const vehicles = content.children();
  const columns = new Array(MAX_COMPARE_SIZE)
    .fill()
    .map((item, index) => {
      const name = `${VEHICLE_PATH_NAME}${index + 1}`;
      const styleId = get(vehicles, `[${index}].vehicles[0].style`);
      const vehicle = get(vehicles, `[${index}]`, parseContent({}));

      // TypeBuyingGuideAd parameters
      const metadata = vehicle.getAllMetadata();
      const { sponsoredLogo, sponsoredImg, displayUrl } = metadata;
      const clickOut = vehicle.metadata('clickOut').boolean();
      const showAdLabel = vehicle.metadata('showAdLabel').boolean();
      const ctaUrl = metadata[`oemClickOutUrl${DEVICE_ID[isMobile]}`] || metadata.ctaUrl;
      const ctaText = metadata[`oemClickOutText${DEVICE_ID[isMobile]}`] || metadata.ctaText;

      return {
        name,
        styleId,
        sponsoredLogo,
        sponsoredImg,
        ctaText,
        ctaUrl,
        displayUrl,
        clickOut,
        showAdLabel,
      };
    })
    .sort(isMobile ? ({ ctaUrl }) => (ctaUrl ? -1 : 1) : noop); // sponsored card is in the first position in mobile

  return (
    <ComparatorModuleWrapper
      {...{
        ...restProps,
        className,
      }}
      content={content}
      isMobile={isMobile}
      columns={columns}
    />
  );
}

ComparatorPreviewModule.propTypes = {
  content: CmsEntities.Content.isRequired,
  isMobile: PropTypes.bool,
  className: PropTypes.string,
};
